import Vue from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { loadConnectAndInitialize } from '@stripe/connect-js'

// Cache pour la clé publique Stripe (valable pour toute la session)
let stripePublicKeyCache = null
// Cache pour l'instance Stripe.js
let stripeInstanceCache = null
// Cache pour l'instance Stripe Connect
let stripeConnectInstanceCache = null
// Promise en cours pour éviter les appels concurrents
let stripeInitPromise = null

class StripeService {
  /**
   * Récupère la clé publique Stripe depuis le backend
   * Utilise un cache en mémoire pour éviter des appels multiples
   * @returns {Promise<string>} La clé publique Stripe
   */
  async getPublicKey() {
    // Si déjà en cache, retourner immédiatement
    if (stripePublicKeyCache) {
      return stripePublicKeyCache
    }

    // Récupérer depuis le backend
    const response = await Vue.auth.fetch({
      url: `/api/payment/stripe/public-key`,
      method: 'get',
    })

    // Extraire la clé (peut être response.data.publicKey ou response.data directement)
    const publicKey = response.data?.publicKey || response.data

    // Mettre en cache pour les prochains appels
    stripePublicKeyCache = publicKey

    return publicKey
  }

  /**
   * Initialise Stripe.js avec la clé publique (via npm package @stripe/stripe-js)
   * @returns {Promise<Stripe>} Instance Stripe initialisée
   */
  async initStripe() {
    // Si l'instance est déjà en cache, la retourner
    if (stripeInstanceCache) {
      return stripeInstanceCache
    }

    // Si une initialisation est déjà en cours, attendre qu'elle se termine
    if (stripeInitPromise) {
      return stripeInitPromise
    }

    // Créer une nouvelle Promise d'initialisation
    stripeInitPromise = (async () => {
      try {
        // Récupérer la clé publique
        const publicKey = await this.getPublicKey()

        if (!publicKey) {
          throw new Error('No Stripe public key available')
        }

        // Charger Stripe via le package npm
        stripeInstanceCache = await loadStripe(publicKey)

        return stripeInstanceCache
      } finally {
        // Réinitialiser la Promise une fois terminée
        stripeInitPromise = null
      }
    })()

    return stripeInitPromise
  }

  /**
   * Initialise Stripe Connect pour les fonctionnalités embedded (via npm package @stripe/connect-js)
   * @param {string} clientSecret - Le client secret pour initialiser Connect
   * @returns {Promise<StripeConnect>} Instance Stripe Connect initialisée
   */
  async initStripeConnect(clientSecret) {
    // Récupérer la clé publique
    const publicKey = await this.getPublicKey()

    if (!publicKey) {
      throw new Error('No Stripe public key available')
    }

    // Charger et initialiser Stripe Connect via le package npm
    const stripeConnect = await loadConnectAndInitialize({
      publishableKey: publicKey,
      fetchClientSecret: async () => clientSecret,
    })

    return stripeConnect
  }

  /**
   * Gère l'authentification 3D Secure ou toute autre action requise
   * @param {string} clientSecret - Le client secret du PaymentIntent
   * @returns {Promise<{paymentIntent, error}>} Résultat de l'authentification
   */
  async handleNextAction(clientSecret) {
    if (!clientSecret) {
      throw new Error('Client secret is required for authentication')
    }

    // Initialiser Stripe si ce n'est pas déjà fait
    const stripe = await this.initStripe()

    if (!stripe) {
      throw new Error('Failed to initialize Stripe')
    }

    // Gérer l'authentification avec Stripe
    // confirmCardPayment va gérer automatiquement :
    // - Les modals 3D Secure (use_stripe_sdk)
    // - Les redirections externes (redirect_to_url)
    // On passe seulement le client_secret, pas de payment_method car déjà attaché
    const result = await stripe.confirmCardPayment(clientSecret)

    return result
  }

  /**
   * Récupère le statut d'un PaymentIntent
   * Utile après une redirection externe pour vérifier le résultat
   * @param {string} clientSecret - Le client secret du PaymentIntent
   * @returns {Promise<{paymentIntent, error}>} PaymentIntent avec son statut actuel
   */
  async retrievePaymentIntent(clientSecret) {
    if (!clientSecret) {
      throw new Error('Client secret is required')
    }

    // Initialiser Stripe si ce n'est pas déjà fait
    const stripe = await this.initStripe()

    if (!stripe) {
      throw new Error('Failed to initialize Stripe')
    }

    // Récupérer le PaymentIntent
    const result = await stripe.retrievePaymentIntent(clientSecret)

    return result
  }

  /**
   * Réinitialise tous les caches (utile pour les tests ou si la config change)
   */
  clearCache() {
    stripePublicKeyCache = null
    stripeInstanceCache = null
    stripeConnectInstanceCache = null
  }
}

export default new StripeService()